// src/components/ChatSeenContacts.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { API, getActiveBranch } from "../apiBase";
import { useAuth } from "../context/AuthContext";
import "./ChatSeenContacts.css";

function getCookie(name) {
  try {
    const m = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
    return m ? decodeURIComponent(m[2]) : "";
  } catch {
    return "";
  }
}

function safeText(v, fallback = "-") {
  const s = String(v ?? "").trim();
  return s || fallback;
}

function fmtDate(v) {
  if (!v) return "-";
  return String(v).slice(0, 10);
}

function dateToMonthDay(v) {
  const s = String(v || "").trim();
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[2]}-${m[3]}` : "";
}

function displayMD(v) {
  const md = dateToMonthDay(v);
  return md ? `-${md}` : "";
}

export default function ChatSeenContacts() {
  const BASE = (API || "").replace(/\/+$/, "");
  const STATIC_VOUCHER_BASE = `${BASE}/static/loyalty_vouchers`;
  const { token } = useAuth();
  const fileRef = useRef(null);

  const normalizeVoucherUrl = useCallback(
    (url) => {
      const raw = String(url || "").trim();
      if (!raw) return "";

      // Correct final target:
      // https://api.bamboome.me/static/loyalty_vouchers/YYYY-MM-DD/file.jpg
      if (raw.includes("/static/loyalty_vouchers/")) return raw;

      // Fix old links that were saved without /static.
      const marker = "/loyalty_vouchers/";
      const pos = raw.indexOf(marker);
      if (pos >= 0) {
        const rest = raw.slice(pos + marker.length);
        return `${STATIC_VOUCHER_BASE}/${rest}`;
      }

      return raw;
    },
    [STATIC_VOUCHER_BASE]
  );

  const openVoucher = useCallback((url) => {
    const cleanUrl = normalizeVoucherUrl(url);
    if (!cleanUrl) return;

    // Cache bypass: when regenerating the same filename, browser/Cloudflare may show the old image.
    const sep = cleanUrl.includes("?") ? "&" : "?";
    window.open(`${cleanUrl}${sep}open=${Date.now()}`, "_blank", "noopener,noreferrer");
  }, [normalizeVoucherUrl]);

  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [voucherBusy, setVoucherBusy] = useState("");
  const [sendBusy, setSendBusy] = useState(false);
  const [templateName, setTemplateName] = useState("first_time_we_met");
  const [languageCode, setLanguageCode] = useState("en");
  const [onlyUnsent, setOnlyUnsent] = useState(true);
  const [createMissingVouchers, setCreateMissingVouchers] = useState(true);
  const [uploadResult, setUploadResult] = useState(null);

  const [stats, setStats] = useState({
    total: 0,
    matched: 0,
    unmatched: 0,
    total_mentions: 0,
    vouchers_created: 0,
    vouchers_redeemed: 0,
    first_date: null,
    last_date: null,
  });

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);

  const [q, setQ] = useState("");
  const [matched, setMatched] = useState("");
  const [visitSort, setVisitSort] = useState("asc");

  // ✅ ONE CALENDAR ONLY: first visit day/month, ignoring year
  const [firstVisitDate, setFirstVisitDate] = useState("");

  const [page, setPage] = useState(0);
  const limit = 100;

  const activeBranch = useMemo(() => {
    try {
      return String(getActiveBranch?.() || "").trim().toLowerCase();
    } catch {
      return "";
    }
  }, []);

  const authHeaders = useMemo(() => {
    const h = {};
    if (token) h.Authorization = `Bearer ${token}`;
    if (activeBranch) {
      h["x-branch"] = activeBranch;
      h["X-Branch"] = activeBranch;
    }
    return h;
  }, [token, activeBranch]);

  const apiJson = useCallback(
    async (path, options = {}) => {
      const method = String(options.method || (options.body ? "POST" : "GET")).toUpperCase();
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";

      const headers = {
        ...authHeaders,
        ...(options.headers || {}),
      };

      if (["POST", "PUT", "PATCH", "DELETE"].includes(method) && csrf) {
        headers["x-csrf-token"] = headers["x-csrf-token"] || csrf;
      }

      const res = await fetch(`${BASE}${path}`, {
        credentials: "include",
        mode: "cors",
        ...options,
        method,
        headers,
      });

      const text = await res.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(data?.detail || data?.message || text || `${res.status} ${res.statusText}`);
      }

      return data || {};
    },
    [BASE, authHeaders]
  );

  const addFiltersToParams = useCallback(() => {
    const params = new URLSearchParams();

    params.set("visit_sort", visitSort);

    // ✅ FORCE FIRST VISIT FILTER ONLY
    params.set("visit_date_field", "first");

    if (q.trim()) params.set("q", q.trim());
    if (matched !== "") params.set("matched", matched);

    // ✅ Send same MM-DD as from/to to make exact day match while ignoring year
    const md = dateToMonthDay(firstVisitDate);
    if (md) {
      params.set("month_day_from", md);
      params.set("month_day_to", md);
    }

    return params;
  }, [q, matched, visitSort, firstVisitDate]);

  const loadStats = useCallback(async () => {
    const data = await apiJson("/chat-seen/stats");

    setStats({
      total: Number(data.total || 0),
      matched: Number(data.matched || 0),
      unmatched: Number(data.unmatched || 0),
      total_mentions: Number(data.total_mentions || 0),
      vouchers_created: Number(data.vouchers_created || 0),
      vouchers_redeemed: Number(data.vouchers_redeemed || 0),
      first_date: data.first_date || null,
      last_date: data.last_date || null,
    });
  }, [apiJson]);

  const loadList = useCallback(async () => {
    const params = addFiltersToParams();
    params.set("limit", String(limit));
    params.set("offset", String(page * limit));

    const data = await apiJson(`/chat-seen/list?${params.toString()}`);

    setItems(Array.isArray(data.items) ? data.items : []);
    setTotal(Number(data.total || 0));
  }, [apiJson, page, addFiltersToParams]);

  const refreshAll = useCallback(async () => {
    setBusy(true);
    try {
      await Promise.all([loadStats(), loadList()]);
    } catch (e) {
      alert("❌ Failed to load Chat Seen Contacts:\n" + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  }, [loadStats, loadList]);

  useEffect(() => {
    const t = setTimeout(() => {
      refreshAll();
    }, 250);

    return () => clearTimeout(t);
  }, [refreshAll]);

  async function copyText(text) {
    try {
      await navigator.clipboard.writeText(text);
      alert("✅ Voucher link copied");
    } catch {
      const input = document.createElement("input");
      input.value = text;
      document.body.appendChild(input);
      input.select();
      document.execCommand("copy");
      document.body.removeChild(input);
      alert("✅ Voucher link copied");
    }
  }

  async function uploadFile() {
    if (!file) {
      alert("Choose a WhatsApp .txt export first.");
      return;
    }

    const fileName = String(file.name || "");

    if (!/\.txt$/i.test(fileName)) {
      alert("Please upload the WhatsApp exported .txt file.");
      return;
    }

    setBusy(true);
    setUploadResult(null);

    try {
      const csrf = getCookie("csrf_token") || getCookie("csrf") || "";
      const form = new FormData();
      form.append("file", file, fileName);

      const headers = { ...authHeaders };
      if (csrf) headers["x-csrf-token"] = csrf;

      const res = await fetch(`${BASE}/chat-seen/upload`, {
        method: "POST",
        credentials: "include",
        mode: "cors",
        headers,
        body: form,
      });

      const text = await res.text().catch(() => "");
      let data = null;

      try {
        data = text ? JSON.parse(text) : null;
      } catch {
        data = null;
      }

      if (!res.ok) {
        throw new Error(data?.detail || data?.message || text || `Upload failed (${res.status})`);
      }

      setUploadResult(data || {});
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      setPage(0);

      await Promise.all([loadStats(), loadList()]);
    } catch (e) {
      alert("❌ Upload failed:\n" + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  }

  async function generateVoucher(x) {
    const rowKey = String(x.id || x.normalized_phone || x.phone || "");

    try {
      const guestName = String(
        x.contact_name || x.detected_name || x.sender || "Guest"
      ).trim();

      const firstVisitDate = String(
        x.first_seen_date || x.last_seen_date || new Date().toISOString().slice(0, 10)
      ).slice(0, 10);

      if (!guestName || guestName === "Guest") {
        alert("❌ This customer has no valid name to print on the voucher.");
        return;
      }

      if (!firstVisitDate || firstVisitDate === "-") {
        alert("❌ This customer has no valid first visit date.");
        return;
      }

      const params = new URLSearchParams();
      params.set("guest_name", guestName);
      params.set("first_visit_date", firstVisitDate);

      if (x.normalized_phone || x.phone) {
        params.set("normalized_phone", x.normalized_phone || x.phone);
      }

      setVoucherBusy(rowKey);

      const data = await apiJson(`/chat-seen/create-voucher?${params.toString()}`, {
        method: "POST",
      });

      await Promise.all([loadStats(), loadList()]);

      alert(
        `✅ Voucher Generated\n\nGuest: ${data.guest_name || guestName}\nCode: ${
          data.voucher_code || "-"
        }\nLink: ${normalizeVoucherUrl(data.voucher_link) || "-"}`
      );
    } catch (e) {
      alert("❌ Voucher generation failed:\n" + (e?.message || String(e)));
    } finally {
      setVoucherBusy("");
    }
  }

  async function generateAllVisibleVouchers() {
    if (!total) {
      alert("No customers found in the current filtered list.");
      return;
    }

    if (!window.confirm(`Generate vouchers for ALL filtered guests (${total} customers)?\n\nThis will also regenerate guests that already have vouchers.`)) return;

    setBusy(true);

    try {
      const params = addFiltersToParams();

      // ✅ IMPORTANT:
      // Generate for ALL filtered guests, not only the current visible page,
      // and do NOT skip guests that already have vouchers.
      params.set("only_missing", "false");
      params.set("overwrite_existing", "true");
      params.set("max_items", String(Math.min(Number(total || 0), 100000)));

      const data = await apiJson(`/chat-seen/create-vouchers-bulk?${params.toString()}`, {
        method: "POST",
      });

      await Promise.all([loadStats(), loadList()]);

      alert(
        `✅ Bulk voucher generation finished.\n\n` +
          `Total matching filter: ${data.total_matching_filter || 0}\n` +
          `Generated/regenerated: ${data.created_or_updated || 0}\n` +
          `Failed: ${data.failed || 0}\n\n` +
          `Vouchers folder:\n${data.vouchers_folder || "-"}`
      );
    } catch (e) {
      alert("❌ Bulk voucher generation failed:\n" + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  }


  async function previewBulkWhatsAppTemplate() {
    if (!total) {
      alert("No customers found in the current filtered list.");
      return;
    }

    setSendBusy(true);
    try {
      const params = addFiltersToParams();
      params.set("template_name", templateName.trim() || "first_time_we_met");
      params.set("language_code", languageCode.trim() || "en");
      params.set("create_missing_vouchers", createMissingVouchers ? "true" : "false");
      params.set("only_unsent", onlyUnsent ? "true" : "false");
      params.set("dry_run", "true");
      params.set("max_items", "5");

      const data = await apiJson(`/chat-seen/send-voucher-template-bulk?${params.toString()}`, {
        method: "POST",
      });

      const first = Array.isArray(data.items) && data.items[0] ? data.items[0] : null;
      alert(
        `✅ Preview ready\n\n` +
          `Matching filter: ${data.total_matching_filter || 0}\n` +
          `Selected after unsent filter: ${data.selected_after_unsent_filter || 0}\n` +
          `Prepared preview: ${data.prepared || 0}\n\n` +
          (first
            ? `Example:\nName: ${first.guest_name}\nPhone: ${first.phone}\nVoucher: ${first.voucher_link}`
            : "No eligible customer found.")
      );
    } catch (e) {
      alert("❌ WhatsApp preview failed:\n" + (e?.message || String(e)));
    } finally {
      setSendBusy(false);
    }
  }

  async function sendBulkWhatsAppTemplate() {
    if (!total) {
      alert("No customers found in the current filtered list.");
      return;
    }

    const maxText = window.prompt(
      `Send WhatsApp template to filtered customers?\n\nTotal filtered: ${total}\nTemplate: ${templateName || "first_time_we_met"}\nLanguage: ${languageCode || "en"}\n\nEnter max customers to send now:`,
      String(Math.min(total, 1000))
    );

    if (maxText === null) return;

    const maxItems = Math.max(1, Math.min(100000, Number(maxText || 0)));
    if (!Number.isFinite(maxItems) || maxItems <= 0) {
      alert("Invalid max customers number.");
      return;
    }

    if (!window.confirm(
      `CONFIRM SEND\n\nThis will send the approved WhatsApp template to up to ${maxItems} customers.\nEach customer gets his own name in {{1}} and his own voucher image in the header.\n\nContinue?`
    )) return;

    setSendBusy(true);
    try {
      const params = addFiltersToParams();
      params.set("template_name", templateName.trim() || "first_time_we_met");
      params.set("language_code", languageCode.trim() || "en");
      params.set("create_missing_vouchers", createMissingVouchers ? "true" : "false");
      params.set("only_unsent", onlyUnsent ? "true" : "false");
      params.set("dry_run", "false");
      params.set("max_items", String(maxItems));

      const data = await apiJson(`/chat-seen/send-voucher-template-bulk?${params.toString()}`, {
        method: "POST",
      });

      await Promise.all([loadStats(), loadList()]);

      alert(
        `✅ WhatsApp bulk send finished.\n\n` +
          `Matching filter: ${data.total_matching_filter || 0}\n` +
          `Processed: ${data.processed || 0}\n` +
          `Sent: ${data.sent || 0}\n` +
          `Failed: ${data.failed || 0}\n` +
          `Skipped: ${data.skipped || 0}`
      );
    } catch (e) {
      alert("❌ WhatsApp bulk send failed:\n" + (e?.message || String(e)));
    } finally {
      setSendBusy(false);
    }
  }

  async function sendSingleWhatsAppTemplate(x) {
    const phone = String(x.normalized_phone || x.phone || "").trim();
    if (!phone) {
      alert("This row has no valid phone.");
      return;
    }

    if (!window.confirm(`Send voucher template to ${safeText(x.contact_name || x.detected_name || phone)}?`)) return;

    const rowKey = String(x.id || phone);
    setVoucherBusy(rowKey);

    try {
      const params = new URLSearchParams();
      params.set("normalized_phone", phone);
      params.set("template_name", templateName.trim() || "first_time_we_met");
      params.set("language_code", languageCode.trim() || "en");
      params.set("create_missing_voucher", "true");
      params.set("dry_run", "false");

      const data = await apiJson(`/chat-seen/send-voucher-template?${params.toString()}`, {
        method: "POST",
      });

      await Promise.all([loadStats(), loadList()]);
      alert(`✅ Sent\n\nName: ${data.guest_name || "-"}\nMessage ID: ${data.message_id || "-"}`);
    } catch (e) {
      alert("❌ Send failed:\n" + (e?.message || String(e)));
    } finally {
      setVoucherBusy("");
    }
  }

  async function sendTestWhatsAppTemplate(x) {
    const phone = String(x.normalized_phone || x.phone || "").trim();
    if (!phone) {
      alert("This row has no valid phone.");
      return;
    }

    const customerName = safeText(x.contact_name || x.detected_name || phone);
    if (!window.confirm(`Send TEST voucher template to 96171565383 using this customer's voucher?

Customer: ${customerName}`)) return;

    const rowKey = String(x.id || phone);
    setVoucherBusy(rowKey);

    try {
      const params = new URLSearchParams();
      params.set("normalized_phone", phone);
      params.set("template_name", templateName.trim() || "first_time_we_met");
      params.set("language_code", languageCode.trim() || "en");
      params.set("create_missing_voucher", "true");
      params.set("dry_run", "false");

      const data = await apiJson(`/chat-seen/send-voucher-template-test?${params.toString()}`, {
        method: "POST",
      });

      await Promise.all([loadStats(), loadList()]);
      alert(
        `✅ Test Sent

` +
          `To: ${data.sent_to || "96171565383"}
` +
          `Customer name in {{1}}: ${data.guest_name || "-"}
` +
          `Voucher image:
${normalizeVoucherUrl(data.voucher_link) || "-"}

` +
          `Message ID: ${data.message_id || "-"}`
      );
    } catch (e) {
      alert("❌ Test send failed:\n" + (e?.message || String(e)));
    } finally {
      setVoucherBusy("");
    }
  }

  function download(path) {
    const params = addFiltersToParams();
    const sep = path.includes("?") ? "&" : "?";
    window.open(`${BASE}${path}${sep}${params.toString()}`, "_blank", "noopener,noreferrer");
  }

  async function clearDb() {
    if (!window.confirm("Clear all Chat Seen Contacts imported data?")) return;

    setBusy(true);

    try {
      await apiJson("/chat-seen/clear", { method: "DELETE" });
      setUploadResult(null);
      setPage(0);
      await Promise.all([loadStats(), loadList()]);
      alert("✅ Chat Seen Contacts cleared.");
    } catch (e) {
      alert("❌ Clear failed:\n" + (e?.message || String(e)));
    } finally {
      setBusy(false);
    }
  }

  function clearFilters() {
    setQ("");
    setMatched("");
    setVisitSort("asc");
    setFirstVisitDate("");
    setPage(0);
  }

  const totalPages = Math.max(1, Math.ceil(total / limit));

  return (
    <main className="chat-seen-page">
      <section className="chat-seen-hero">
        <div>
          <div className="cs-kicker">WhatsApp Intelligence</div>
          <h1>👁 Chat Seen Contacts</h1>
          <p>
            Upload WhatsApp chat exports, detect phone numbers, match contacts.db,
            filter by first visit day/month, and generate loyalty vouchers directly from the filtered table.
          </p>
        </div>

        <div className="chat-seen-actions">
          <button className="cs-btn" onClick={refreshAll} disabled={busy}>
            {busy ? "⏳ Loading..." : "🔄 Refresh"}
          </button>

          <button className="cs-btn primary" onClick={generateAllVisibleVouchers} disabled={busy || total <= 0}>
            🎁 Generate All Filtered Guests
          </button>

          <button className="cs-btn primary" onClick={sendBulkWhatsAppTemplate} disabled={busy || sendBusy || total <= 0}>
            {sendBusy ? "⏳ Sending..." : "📲 Send WhatsApp Template"}
          </button>

          <button className="cs-btn" onClick={previewBulkWhatsAppTemplate} disabled={busy || sendBusy || total <= 0}>
            👀 Preview Send
          </button>

          <button className="cs-btn" onClick={() => download("/chat-seen/export-csv")}>
            📄 Export CSV
          </button>

          <button className="cs-btn" onClick={() => download("/chat-seen/download-db")}>
            💾 Download DB
          </button>

          <button className="cs-btn danger" onClick={clearDb} disabled={busy}>
            🗑 Clear
          </button>
        </div>
      </section>

      <section className="cs-grid">
        <div className="cs-card cs-upload">
          <h3>📤 Upload WhatsApp Export</h3>
          <p className="cs-muted">
            Choose the exported <b>.txt</b> file from WhatsApp.
          </p>

          <div className="cs-upload-row">
            <input
              ref={fileRef}
              type="file"
              accept=".txt,text/plain"
              onChange={(e) => setFile(e.target.files?.[0] || null)}
            />

            <button className="cs-btn primary" onClick={uploadFile} disabled={busy || !file}>
              {busy ? "⏳ Processing..." : "🚀 Detect Numbers"}
            </button>
          </div>

          {file && <div className="cs-selected">Selected: {file.name}</div>}

          {uploadResult && (
            <div className="cs-result">
              <b>✅ Import completed</b>
              <span>Numbers detected: {uploadResult.numbers_detected ?? 0}</span>
              <span>Saved/updated: {uploadResult.saved_or_updated ?? 0}</span>
              <span>Matched in contacts.db: {uploadResult.matched_in_contacts_db ?? 0}</span>
            </div>
          )}
        </div>

        <div className="cs-card cs-upload">
          <h3>📲 WhatsApp Voucher Template</h3>
          <p className="cs-muted">
            Sends your approved 360dialog template. Header image = customer voucher. Body <b>{"{{1}}"}</b> = customer name. Button stays as your one static <b>More Info</b> button.
          </p>

          <div className="cs-upload-row">
            <input
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              placeholder="Template name, example: first_time_we_met"
            />
            <input
              value={languageCode}
              onChange={(e) => setLanguageCode(e.target.value)}
              placeholder="Language, example: en"
              style={{ maxWidth: 150 }}
            />
          </div>

          <label className="cs-check-row">
            <input
              type="checkbox"
              checked={createMissingVouchers}
              onChange={(e) => setCreateMissingVouchers(e.target.checked)}
            />
            Create missing vouchers before sending
          </label>

          <label className="cs-check-row">
            <input
              type="checkbox"
              checked={onlyUnsent}
              onChange={(e) => setOnlyUnsent(e.target.checked)}
            />
            Send only customers not already marked as sent
          </label>
        </div>

        <div className="cs-stat">
          <span>Total</span>
          <b>{stats.total}</b>
        </div>

        <div className="cs-stat">
          <span>Matched</span>
          <b>{stats.matched}</b>
        </div>

        <div className="cs-stat">
          <span>Unmatched</span>
          <b>{stats.unmatched}</b>
        </div>

        <div className="cs-stat">
          <span>Vouchers</span>
          <b>{stats.vouchers_created}</b>
        </div>
      </section>

      <section className="cs-card">
        <div className="cs-toolbar">
          <div>
            <h3>📋 Results</h3>
            <p className="cs-muted">
              Database date range: {safeText(fmtDate(stats.first_date))} →{" "}
              {safeText(fmtDate(stats.last_date))}
            </p>
          </div>

          <div className="cs-count">
            Showing {items.length} / {total} records
          </div>
        </div>

        <div className="cs-filters">
          <input
            placeholder="Search phone, contact name, detected name, sender, voucher code..."
            value={q}
            onChange={(e) => {
              setQ(e.target.value);
              setPage(0);
            }}
          />

          <select
            value={matched}
            onChange={(e) => {
              setMatched(e.target.value);
              setPage(0);
            }}
          >
            <option value="">All matches</option>
            <option value="1">Matched only</option>
            <option value="0">Unmatched only</option>
          </select>

          <select
            value={visitSort}
            onChange={(e) => {
              setVisitSort(e.target.value);
              setPage(0);
            }}
          >
            <option value="asc">Smallest Visits → Biggest Visits</option>
            <option value="desc">Biggest Visits → Smallest Visits</option>
          </select>

          <label className="cs-date-label">
            First Visit Day {displayMD(firstVisitDate)}
            <input
              type="date"
              value={firstVisitDate}
              onChange={(e) => {
                setFirstVisitDate(e.target.value);
                setPage(0);
              }}
            />
          </label>

          <button className="cs-btn" onClick={clearFilters} disabled={busy}>
            ✖ Clear Filters
          </button>
        </div>

        <p className="cs-muted">
          The selected calendar year is ignored. Example: choosing <b>2026-06-16</b>{" "}
          filters all customers whose first visit is <b>-06-16</b>, from any year.
        </p>

        <div className="cs-table-wrap">
          <table className="cs-table">
            <thead>
              <tr>
                <th>Phone</th>
                <th>Contact Name</th>
                <th>Detected Name</th>
                <th>Sender</th>
                <th>First Visit</th>
                <th>Last Visit</th>
                <th>Visits</th>
                <th>Match</th>
                <th>WA Send</th>
                <th>Voucher</th>
              </tr>
            </thead>

            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td colSpan="10" className="cs-empty">
                    No records found.
                  </td>
                </tr>
              ) : (
                items.map((x) => {
                  const rowKey = String(x.id || x.normalized_phone || x.phone || "");
                  const voucherUrl = normalizeVoucherUrl(x.voucher_link || x.auto_voucher_link || "");
                  const hasVoucher = Boolean(voucherUrl);

                  return (
                    <tr key={rowKey}>
                      <td className="cs-phone">{safeText(x.normalized_phone || x.phone)}</td>
                      <td>{safeText(x.contact_name)}</td>
                      <td>{safeText(x.detected_name)}</td>
                      <td>{safeText(x.sender)}</td>
                      <td>{fmtDate(x.first_seen_date)}</td>
                      <td>{fmtDate(x.last_seen_date)}</td>
                      <td>
                        <b>{Number(x.times_seen || 0)}</b>
                      </td>
                      <td>
                        {x.matched_in_contacts_db ? (
                          <span className="cs-badge ok">Matched</span>
                        ) : (
                          <span className="cs-badge bad">Unmatched</span>
                        )}
                      </td>
                      <td>
                        <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 160 }}>
                          {x.wa_template_status === "sent" ? (
                            <span className="cs-badge ok">Sent</span>
                          ) : x.wa_template_status === "failed" ? (
                            <span className="cs-badge bad">Failed</span>
                          ) : (
                            <span className="cs-badge">Not sent</span>
                          )}
                          <small style={{ color: "#000", fontWeight: 700 }}>{safeText(x.wa_template_sent_at, "")}</small>
                          <button
                            className="cs-btn"
                            onClick={() => sendSingleWhatsAppTemplate(x)}
                            disabled={voucherBusy === rowKey || sendBusy}
                          >
                            📲 Send
                          </button>
                          <button
                            className="cs-btn"
                            onClick={() => sendTestWhatsAppTemplate(x)}
                            disabled={voucherBusy === rowKey || sendBusy}
                            title="Send this row's full template to test number 96171565383"
                          >
                            🧪 Test 96171565383
                          </button>
                        </div>
                      </td>
                      <td>
                        {hasVoucher ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6, minWidth: 260 }}>
                            <button
                              className="cs-btn"
                              onClick={() => openVoucher(voucherUrl)}
                            >
                              👁 View Voucher
                            </button>

                            <input
                              type="text"
                              readOnly
                              value={voucherUrl}
                              onClick={(e) => e.target.select()}
                              style={{
                                width: "100%",
                                padding: "8px 10px",
                                borderRadius: "10px",
                                border: "1px solid #d1d5db",
                                background: "#ffffff",
                                color: "#000000",
                                fontSize: "12px",
                                fontWeight: 600,
                              }}
                            />

                            <button className="cs-btn" onClick={() => copyText(voucherUrl)}>
                              📋 Copy Link
                            </button>

                            <small style={{ color: "#000", fontWeight: 700 }}>
                              Code: {safeText(x.voucher_code, "-")}
                            </small>

                            <button
                              className="cs-btn primary"
                              onClick={() => generateVoucher(x)}
                              disabled={voucherBusy === rowKey}
                            >
                              {voucherBusy === rowKey ? "⏳ Generating..." : "🔁 Regenerate"}
                            </button>
                          </div>
                        ) : (
                          <button
                            className="cs-btn primary"
                            onClick={() => generateVoucher(x)}
                            disabled={voucherBusy === rowKey}
                          >
                            {voucherBusy === rowKey ? "⏳ Generating..." : "🎁 Generate Voucher"}
                          </button>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="cs-pagination">
          <button
            className="cs-btn"
            disabled={page <= 0 || busy}
            onClick={() => setPage((p) => Math.max(0, p - 1))}
          >
            Previous
          </button>

          <span>
            Page {page + 1} / {totalPages}
          </span>

          <button
            className="cs-btn"
            disabled={page + 1 >= totalPages || busy}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </div>
      </section>
    </main>
  );
}